import { PayloadAction } from '@reduxjs/toolkit';
import { put } from 'redux-saga/effects';

import * as API from 'services';

import {
	formatUploadExcelRecommenderFailedList,
	formatWorkerRecommenderApplicantList,
	formatWorkerRecommenderDetail,
	formatWorkerRecommenderList,
	formatWorkerRecommenderLodgingList,
} from './worker-recommender.helper';
import { WorkerRecommenderActions } from './worker-recommender.redux';
import {
	IPayloadCreateWorkerRecommenderApplicantRequest,
	IPayloadCreateWorkerRecommenderRequest,
	IPayloadDeleteWorkerRecommenderApplicantRequest,
	IPayloadDeleteWorkerRecommenderLodgingByIdRequest,
	IPayloadDeleteWorkerRecommendersRequest,
	IPayloadGetWorkerRecommenderApplicantListRequest,
	IPayloadGetWorkerRecommenderDetailRequest,
	IPayloadGetWorkerRecommenderExcelListRequest,
	IPayloadGetWorkerRecommenderListRequest,
	IPayloadGetWorkerRecommenderLodgingListRequest,
	TPayloadUploadExcelRecommenderRequest,
} from './worker-recommender.type';

export function* getWorkerRecommenderListSaga(
	action: PayloadAction<IPayloadGetWorkerRecommenderListRequest>
) {
	const { callback, searchTypes, ...rest } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetWorkerRecommenderListResponse> =
			yield API.getWorkerRecommenderList({ searchTypes, ...rest });
		const workerRecommenderList = formatWorkerRecommenderList(
			res?.data?.content
		);

		yield put(
			WorkerRecommenderActions.getWorkerRecommenderListSuccess({
				...rest,
				workerRecommenderList,
				total: res?.data?.total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerRecommenderActions.getWorkerRecommenderListFailure());
	}
}

export function* getWorkerRecommenderExcelListSaga(
	action: PayloadAction<IPayloadGetWorkerRecommenderExcelListRequest>
) {
	const { callback, searchTypes, ...rest } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetWorkerRecommenderListResponse> =
			yield API.getWorkerRecommenderList({ searchTypes, ...rest });
		const content = formatWorkerRecommenderList(res?.data?.content);

		yield put(WorkerRecommenderActions.getWorkerRecommenderExcelListSuccess());
		callback?.({ data: content });
	} catch (error) {
		callback?.({ error });
		yield put(WorkerRecommenderActions.getWorkerRecommenderExcelListFailure());
	}
}

export function* getWorkerRecommenderDetail(
	action: PayloadAction<IPayloadGetWorkerRecommenderDetailRequest>
) {
	const { id, callback } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.IWorkerRecommenderItem> =
			yield API.getWorkerRecommenderDetail(id);

		const workerRecommenderDetail = formatWorkerRecommenderDetail(res?.data);

		yield put(
			WorkerRecommenderActions.getWorkerRecommenderDetailSuccess({
				workerRecommenderDetail,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerRecommenderActions.getWorkerRecommenderDetailFailure());
	}
}

export function* deleteWorkerRecommenders(
	action: PayloadAction<IPayloadDeleteWorkerRecommendersRequest>
) {
	const { ids, callback } = action.payload;

	try {
		yield API.deleteWorkerRecommenders(ids);

		yield put(WorkerRecommenderActions.deleteWorkerRecommendersSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerRecommenderActions.deleteWorkerRecommendersFailure());
	}
}

export function* createWorkerRecommenderSaga(
	action: PayloadAction<IPayloadCreateWorkerRecommenderRequest>
) {
	const { callback, ...props } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.IWorkerRecommenderWhenRequestCreateSuccess> =
			yield API.createWorkerRecommender({
				...props,
			});

		yield put(WorkerRecommenderActions.createWorkerRecommenderSuccess());
		callback?.(res?.data as number);
	} catch (error) {
		callback?.(undefined, error);
		yield put(WorkerRecommenderActions.createWorkerRecommenderFailure());
	}
}

export function* getWorkerRecommenderApplicantListSaga(
	action: PayloadAction<IPayloadGetWorkerRecommenderApplicantListRequest>
) {
	const { callback, ...rest } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetWorkerRecommenderApplicantListResponse> =
			yield API.getWorkerRecommenderApplicantList({
				...rest,
				programId: rest?.programId || null,
			});
		const applicantList = formatWorkerRecommenderApplicantList(
			res?.data?.content
		);
		yield put(
			WorkerRecommenderActions.getWorkerRecommenderApplicantListSuccess({
				...rest,
				applicantList,
				applicantTotal: res?.data?.total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(
			WorkerRecommenderActions.getWorkerRecommenderApplicantListFailure()
		);
	}
}

export function* getAllWorkerRecommenderApplicantListSaga(
	action: PayloadAction<IPayloadGetWorkerRecommenderApplicantListRequest>
) {
	const { callback, ...rest } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetWorkerRecommenderApplicantListResponse> =
			yield API.getWorkerRecommenderApplicantList({
				...rest,
				programId: rest?.programId || null,
			});
		const applicantList = formatWorkerRecommenderApplicantList(
			res?.data?.content
		);

		yield put(
			WorkerRecommenderActions.getAllWorkerRecommenderApplicantListSuccess({
				...rest,
				applicantList,
				applicantTotal: res?.data?.total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(
			WorkerRecommenderActions.getAllWorkerRecommenderApplicantListFailure()
		);
	}
}

export function* createWorkerRecommenderApplicantSaga(
	action: PayloadAction<IPayloadCreateWorkerRecommenderApplicantRequest>
) {
	const { callback, ...rest } = action.payload;

	try {
		yield API.createWorkerRecommenderApplicant({
			...rest,
		});

		yield put(
			WorkerRecommenderActions.createWorkerRecommenderApplicantSuccess()
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(
			WorkerRecommenderActions.createWorkerRecommenderApplicantFailure()
		);
	}
}

export function* deleteWorkerRecommenderApplicantSaga(
	action: PayloadAction<IPayloadDeleteWorkerRecommenderApplicantRequest>
) {
	const { ids, recommenderId, callback } = action.payload;

	try {
		yield API.deleteWorkerRecommenderApplicant(recommenderId, ids);

		yield put(
			WorkerRecommenderActions.deleteWorkerRecommenderApplicantSuccess()
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(
			WorkerRecommenderActions.deleteWorkerRecommenderApplicantFailure()
		);
	}
}

export function* uploadExcelRecommenderSaga(
	action: PayloadAction<TPayloadUploadExcelRecommenderRequest>
) {
	const { data, callback } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.TUploadExcelRecommenderResponse> =
			yield API.uploadExcelRecommender(data);

		const failedList = formatUploadExcelRecommenderFailedList(res?.data);
		const failCount = failedList?.length || 0;
		const successCount = data?.length - (failCount || 0);

		yield put(
			WorkerRecommenderActions.uploadExcelRecommenderSuccess({
				failedList,
				successCount,
				failCount,
			})
		);
		callback?.({
			failCount,
			successCount,
		});
	} catch (error: any) {
		callback?.(error);
		yield put(WorkerRecommenderActions.uploadExcelRecommenderFailure());
	}
}

export function* getWorkerRecommenderLodgingListSaga(
	action: PayloadAction<IPayloadGetWorkerRecommenderLodgingListRequest>
) {
	const { callback, ...rest } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetWorkerRecommenderLodgingListResponse> =
			yield API.getWorkerRecommenderLodgingList({ ...rest });

		const content = formatWorkerRecommenderLodgingList(res.data.content);
		yield put(
			WorkerRecommenderActions.getWorkerRecommenderLodgingListSuccess({
				...rest,
				content,
				total: res?.data?.total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(
			WorkerRecommenderActions.getWorkerRecommenderLodgingListFailure()
		);
	}
}

export function* deleteWorkerRecommenderLodgingSaga(
	action: PayloadAction<IPayloadDeleteWorkerRecommenderLodgingByIdRequest>
) {
	const { callback, recommenderId, ids } = action.payload;

	try {
		yield API.deleteWorkerRecommenderLodging(recommenderId, ids);

		yield put(WorkerRecommenderActions.deleteWorkerRecommenderLodgingSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerRecommenderActions.deleteWorkerRecommenderLodgingFailure());
	}
}
